import { useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Activity, Briefcase, Users } from 'lucide-react';
import { usePlaces, flattenPlaces, useDashboardStats, usePersons } from '@/api';
import { useDashboardStore } from '@/stores/useDashboardStore';
import { StatsGrid } from '@/components/dashboard/StatsGrid';
import { LeadsList } from '@/components/dashboard/LeadsList';
import { PersonsList } from '@/components/dashboard/PersonsList';
import { DetailModal } from '@/components/dashboard/DetailModal';
import type { Lead, Person, DashboardStats } from '@/types/leads';
import styles from './Dashboard.module.css';

export const Dashboard = () => {
  const queryClient = useQueryClient();
  const { data: placesData, isLoading: placesLoading, fetchNextPage, hasNextPage } = usePlaces();
  const places = useMemo(() => flattenPlaces(placesData), [placesData]);
  const { data: persons = [], isLoading: personsLoading } = usePersons();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();

  // Auto-fetch all pages for the dashboard
  useEffect(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const {
    selectedItem,
    modalType,
    modalOpen,
    openPlaceModal,
    openPersonModal,
    closeModal,
    navigateToPlace,
    navigateToPerson,
  } = useDashboardStore();

  const isLoading = placesLoading || personsLoading || statsLoading;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['places'] });
    queryClient.invalidateQueries({ queryKey: ['persons'] });
  };

  const handleNavigateToPlace = (placeId: string) => {
    navigateToPlace(placeId, places);
  };

  const handleNavigateToPerson = (personId: number) => {
    navigateToPerson(personId, persons);
  };

  const defaultStats: DashboardStats = {
    total_places: 0,
    total_persons: 0,
    places_with_phone: 0,
    places_with_persons: 0,
    reachable_businesses: 0,
    emails_sent: 0,
    emails_delivered: 0,
    emails_opened: 0,
    email_open_rate: 0,
    whatsapp_sent: 0,
    whatsapp_delivered: 0,
    whatsapp_read: 0,
    suppressed_contacts: 0,
    interested_contacts: 0,
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.content}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerText}>
              <h1 className={styles.title}>Overview</h1>
              <p className={styles.subtitle}>Lead generation &amp; outreach dashboard.</p>
            </div>
            <div className={styles.headerActions}>
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className={`${styles.refreshButton} ${isLoading ? styles.refreshButtonLoading : ''}`}
              >
                <RefreshCw
                  className={`${styles.refreshIcon} ${isLoading ? styles.spinning : ''}`}
                />
                {isLoading ? 'Refreshing...' : 'Refresh'}
              </button>
              <span className={styles.activeBadge}>
                <Activity className={styles.activeBadgeIcon} />
                Active
              </span>
            </div>
          </div>

          {/* Stats */}
          <StatsGrid stats={stats || defaultStats} isLoading={statsLoading} />

          {/* Content grid */}
          <div className={styles.mainGrid}>
            <div className={styles.leadsCard}>
              <div className={styles.cardHeader}>
                <div className={styles.cardHeaderText}>
                  <h2 className={styles.cardTitle}>Business Leads</h2>
                  <p className={styles.cardDescription}>Organizations identified for outreach.</p>
                </div>
                <Briefcase className={styles.cardHeaderIcon} />
              </div>
              <div className={styles.cardBody}>
                {placesLoading ? (
                  <div className={styles.skeletonContainer}>
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className={styles.skeletonRow}>
                        <div className={styles.skeletonLines}>
                          <div className={`${styles.skeletonLine} ${styles.skeletonLineMd}`} />
                          <div className={`${styles.skeletonLine} ${styles.skeletonLineSm}`} />
                        </div>
                        <div className={`${styles.skeletonLine} ${styles.skeletonBadge}`} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <LeadsList leads={places} onSelect={(lead: Lead) => openPlaceModal(lead)} />
                )}
              </div>
            </div>

            <div className={styles.personsCard}>
              <div className={styles.cardHeader}>
                <div className={styles.cardHeaderText}>
                  <h2 className={styles.cardTitle}>Contacts</h2>
                  <p className={styles.cardDescription}>Verified professional contacts.</p>
                </div>
                <Users className={styles.cardHeaderIcon} />
              </div>
              <div className={styles.cardBody}>
                {personsLoading ? (
                  <div className={styles.skeletonContainer}>
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className={styles.skeletonCard}>
                        <div className={styles.skeletonAvatar} />
                        <div className={styles.skeletonLines}>
                          <div className={`${styles.skeletonLine} ${styles.skeletonLineMd}`} />
                          <div className={`${styles.skeletonLine} ${styles.skeletonLineSm}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <PersonsList
                    persons={persons}
                    onSelect={(person: Person) => openPersonModal(person)}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Modal */}
          <DetailModal
            isOpen={modalOpen}
            onClose={closeModal}
            item={selectedItem}
            type={modalType}
            onNavigateToPlace={handleNavigateToPlace}
            onNavigateToPerson={handleNavigateToPerson}
          />
        </div>
      </div>
    </div>
  );
};
